import { useEffect, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import toast from "react-hot-toast";
import { db } from "../firebase";
import { formatCurrency } from "../lib/affiliateAnalytics";

export default function WithdrawPage({ user, analytics, onRefresh }) {
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState("");
  const [wallets, setWallets] = useState([]);
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const walletsQuery = query(collection(db, "wallets"), where("userId", "==", user.uid));
    return onSnapshot(walletsQuery, (snapshot) => {
      setWallets(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const historyQuery = query(
      collection(db, "withdraw_requests"),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(historyQuery, (snapshot) => {
      setHistory(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
    });
  }, [user?.uid]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      toast.error("Enter a valid withdrawal amount.");
      return;
    }
    if (!wallet.trim()) {
      toast.error("Add a wallet address.");
      return;
    }
    if (numericAmount > Number(analytics?.totals?.balance || 0)) {
      toast.error("Withdrawal amount exceeds available affiliate balance.");
      return;
    }

    setSubmitting(true);
    try {
      const userRef = doc(db, "users", user.uid);
      await runTransaction(db, async (transaction) => {
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists()) {
          throw new Error("Affiliate wallet not found.");
        }
        const data = userSnap.data() || {};
        const unlocked = Number(data.affiliateBonusUnlocked || 0);
        const currentBalance = Number(data.balance || 0);
        const pending = Number(data.affiliatePendingWithdrawals || 0);

        if (numericAmount > unlocked) {
          throw new Error("Withdrawal amount exceeds available affiliate balance.");
        }

        transaction.update(userRef, {
          balance: Number((currentBalance - numericAmount).toFixed(2)),
          affiliateBonusUnlocked: Number((unlocked - numericAmount).toFixed(2)),
          affiliatePendingWithdrawals: Number((pending + numericAmount).toFixed(2)),
        });
      });

      await addDoc(collection(db, "withdraw_requests"), {
        userId: user.uid,
        amount: numericAmount,
        wallet: wallet.trim(),
        status: "pending",
        createdAt: serverTimestamp(),
        requestedAt: new Date(),
        requestType: "affiliate_bonus",
        fundsReserved: true,
      });

      toast.success("Affiliate withdrawal request submitted.");
      setAmount("");
      setWallet("");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.message || "Failed to submit withdrawal.");
    } finally {
      setSubmitting(false);
    }
  };

  const affiliateHistory = history.filter(
    (item) => !item.requestType || item.requestType === "affiliate_bonus",
  );

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <form
          onSubmit={handleSubmit}
          className="rounded-[32px] border border-white/10 bg-[rgba(11,18,31,0.92)] p-6 shadow-[0_30px_80px_rgba(0,0,0,0.28)]"
        >
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--aff-muted)]">
            Withdraw
          </div>
          <h2 className="mt-2 text-2xl font-black text-white">Request an affiliate payout</h2>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <InfoCard label="Available balance" value={formatCurrency(analytics?.totals?.unlockedBonus)} />
            <InfoCard label="Locked bonus" value={formatCurrency(analytics?.totals?.lockedBonus)} />
            <InfoCard label="Pending withdrawals" value={formatCurrency(analytics?.totals?.pendingWithdrawals)} />
            <InfoCard label="All-time earnings" value={formatCurrency(analytics?.totals?.earnings)} />
          </div>

          <div className="mt-6 space-y-4">
            <Field label="Amount (USD)">
              <input
                type="number"
                min="1"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="Enter withdrawal amount"
                className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white placeholder:text-slate-500 focus:border-emerald-400/40 focus:outline-none"
              />
            </Field>

            <Field label="Saved wallet">
              <select
                value={wallet}
                onChange={(event) => setWallet(event.target.value)}
                className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white focus:border-emerald-400/40 focus:outline-none"
              >
                <option value="" className="bg-slate-900">
                  Select a wallet
                </option>
                {wallets.map((entry) => (
                  <option key={entry.id} value={entry.address} className="bg-slate-900">
                    {entry.label ? `${entry.label} - ` : ""}
                    {entry.address}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Or paste wallet address">
              <input
                type="text"
                value={wallet}
                onChange={(event) => setWallet(event.target.value)}
                placeholder="TRC20 / BEP20 / ERC20 wallet address"
                className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white placeholder:text-slate-500 focus:border-emerald-400/40 focus:outline-none"
              />
            </Field>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-6 rounded-2xl bg-[var(--aff-accent)] px-5 py-3 text-sm font-bold text-slate-950 disabled:opacity-60"
          >
            {submitting ? "Submitting..." : "Submit withdrawal"}
          </button>
        </form>

        <div className="rounded-[32px] border border-white/10 bg-[rgba(11,18,31,0.92)] p-6 shadow-[0_30px_80px_rgba(0,0,0,0.28)]">
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--aff-muted)]">
            Help
          </div>
          <h3 className="mt-2 text-xl font-black text-white">Affiliate payout checklist</h3>
          <ul className="mt-5 space-y-3 text-sm leading-7 text-slate-300">
            <li>Only unlocked affiliate bonus can be withdrawn from this page.</li>
            <li>Locked affiliate bonus stays in the wallet but unlocks only after 100x turnover.</li>
            <li>When you submit, funds are reserved immediately until finance processes the request.</li>
            <li>If a request is cancelled, the reserved amount should return to your affiliate wallet.</li>
          </ul>
        </div>
      </section>

      <section className="overflow-hidden rounded-[32px] border border-white/10 bg-[rgba(11,18,31,0.92)] shadow-[0_30px_80px_rgba(0,0,0,0.28)]">
        <div className="border-b border-white/10 px-6 py-5">
          <div className="text-xs uppercase tracking-[0.28em] text-[var(--aff-muted)]">
            History
          </div>
          <h3 className="mt-2 text-xl font-black text-white">Recent withdrawal requests</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-white/10 bg-white/[0.03] text-[11px] uppercase tracking-[0.24em] text-[var(--aff-muted)]">
              <tr>
                <th className="px-5 py-4">Order</th>
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Wallet</th>
                <th className="px-5 py-4">Amount</th>
              </tr>
            </thead>
            <tbody>
              {affiliateHistory.length ? (
                affiliateHistory.map((item) => (
                  <tr key={item.id} className="border-b border-white/6 text-slate-200">
                    <td className="px-5 py-4 font-semibold text-white">#{item.id.slice(-8)}</td>
                    <td className="px-5 py-4 text-slate-400">
                      {item.createdAt?.toDate?.()?.toLocaleString() || (item.requestedAt ? new Date(item.requestedAt).toLocaleString() : "-")}
                    </td>
                    <td className="px-5 py-4">
                      <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs font-semibold text-white">
                        {item.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-300">{item.wallet}</td>
                    <td className="px-5 py-4 font-semibold text-emerald-300">
                      {formatCurrency(item.amount)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-sm text-slate-400">
                    No withdrawal requests yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-slate-300">{label}</span>
      {children}
    </label>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
      <div className="text-xs uppercase tracking-[0.18em] text-[var(--aff-muted)]">{label}</div>
      <div className="mt-1 text-xl font-black text-white">{value}</div>
    </div>
  );
}
